import { useEffect, useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, Target, Phone, Mail, MapPin, 
  Calendar, Clock, Building, Flame, Thermometer, Snowflake,
  BarChart3, PieChart, RefreshCw, Filter, Download, Search,
  MessageCircle, Check, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LeadsDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [whatsappQueue, setWhatsappQueue] = useState([]);
  const [showWhatsappQueue, setShowWhatsappQueue] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      return;
    }
    fetchDashboard();
    fetchLeads();
    fetchWhatsappQueue();
  }, [user, authLoading, navigate, token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/leads/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchWhatsappQueue = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/whatsapp-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappQueue(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching WhatsApp queue:', error);
    }
  };

  const markWhatsappSent = async (phone) => {
    try {
      await axios.put(`${API_URL}/admin/whatsapp-queue/${phone}/mark-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as sent');
      fetchWhatsappQueue();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const openWhatsApp = (phone, message) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const fetchLeads = async (status = null) => {
    setLoading(true);
    try {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      const response = await axios.get(`${API_URL}/admin/leads${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchLeads(status);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/leads/${leadId}/status?new_status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Lead status updated to ${newStatus}`);
      fetchLeads(statusFilter);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update lead status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'hot': return 'bg-red-100 text-red-800 border-red-200';
      case 'warm': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cold': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'converted': return 'bg-green-100 text-green-800 border-green-200';
      case 'lost': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'hot': return <Flame className="w-4 h-4" />;
      case 'warm': return <Thermometer className="w-4 h-4" />;
      case 'cold': return <Snowflake className="w-4 h-4" />;
      default: return null;
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(search) ||
      lead.company?.toLowerCase().includes(search) ||
      lead.city?.toLowerCase().includes(search) ||
      lead.phone?.includes(search)
    );
  });

  const exportLeads = () => {
    const csv = [
      ['Name', 'Company', 'Phone', 'Email', 'City', 'Budget', 'Timeline', 'Score', 'Status', 'Created'].join(','),
      ...filteredLeads.map(l => [
        l.name, l.company, l.phone, l.email, l.city, l.budget_range, l.timeline, l.lead_score, l.lead_status, 
        new Date(l.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c5a059]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Leads KPI Dashboard</h1>
            <p className="text-gray-600 mt-1">Track and manage your sales leads</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { fetchDashboard(); fetchLeads(statusFilter); fetchWhatsappQueue(); }}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            {dashboard?.kpis?.whatsapp_pending > 0 && (
              <Button 
                variant="outline"
                onClick={() => setShowWhatsappQueue(!showWhatsappQueue)}
                className="flex items-center gap-2 border-green-500 text-green-600 hover:bg-green-50"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Queue ({dashboard.kpis.whatsapp_pending})
              </Button>
            )}
            <Button 
              onClick={exportLeads}
              className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#b8934d] text-black"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* WhatsApp Queue Panel */}
        {showWhatsappQueue && whatsappQueue.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-green-800 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                WhatsApp Follow-up Queue
              </h3>
              <button 
                onClick={() => setShowWhatsappQueue(false)}
                className="text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {whatsappQueue.map((msg, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 border border-green-100 flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{msg.name}</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {msg.phone}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(msg.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-xs text-gray-600 flex-1 max-h-20 overflow-y-auto">
                    {msg.message}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(msg.message)}
                      className="text-xs"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => openWhatsApp(msg.phone, msg.message)}
                      className="bg-green-500 hover:bg-green-600 text-white text-xs"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Send
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => markWhatsappSent(msg.phone)}
                      className="text-xs border-green-500 text-green-600"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Done
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPI Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-900">{dashboard.summary.total_leads}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Today: {dashboard.summary.today_leads} | This week: {dashboard.summary.week_leads}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Hot Leads</p>
                  <p className="text-3xl font-bold text-red-600">{dashboard.lead_status.hot}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Flame className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {dashboard.kpis.hot_lead_percentage}% of total leads
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Lead Score</p>
                  <p className="text-3xl font-bold text-[#c5a059]">{dashboard.kpis.avg_lead_score}</p>
                </div>
                <div className="w-12 h-12 bg-[#c5a059]/20 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#c5a059]" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Capture rate: {dashboard.kpis.lead_capture_rate}%
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Chat Sessions</p>
                  <p className="text-3xl font-bold text-green-600">{dashboard.summary.total_chats}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                WhatsApp pending: {dashboard.kpis.whatsapp_pending}
              </p>
            </div>
          </div>
        )}

        {/* Status Distribution */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <button 
              onClick={() => handleStatusFilter('hot')}
              className={`p-4 rounded-xl border-2 transition-all ${statusFilter === 'hot' ? 'border-red-500 bg-red-50' : 'border-gray-100 bg-white hover:border-red-200'}`}
            >
              <div className="flex items-center gap-3">
                <Flame className="w-8 h-8 text-red-500" />
                <div className="text-left">
                  <p className="text-2xl font-bold text-red-600">{dashboard.lead_status.hot}</p>
                  <p className="text-sm text-gray-500">Hot Leads (61+ score)</p>
                </div>
              </div>
            </button>

            <button 
              onClick={() => handleStatusFilter('warm')}
              className={`p-4 rounded-xl border-2 transition-all ${statusFilter === 'warm' ? 'border-orange-500 bg-orange-50' : 'border-gray-100 bg-white hover:border-orange-200'}`}
            >
              <div className="flex items-center gap-3">
                <Thermometer className="w-8 h-8 text-orange-500" />
                <div className="text-left">
                  <p className="text-2xl font-bold text-orange-600">{dashboard.lead_status.warm}</p>
                  <p className="text-sm text-gray-500">Warm Leads (26-60 score)</p>
                </div>
              </div>
            </button>

            <button 
              onClick={() => handleStatusFilter('cold')}
              className={`p-4 rounded-xl border-2 transition-all ${statusFilter === 'cold' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-white hover:border-blue-200'}`}
            >
              <div className="flex items-center gap-3">
                <Snowflake className="w-8 h-8 text-blue-500" />
                <div className="text-left">
                  <p className="text-2xl font-bold text-blue-600">{dashboard.lead_status.cold}</p>
                  <p className="text-sm text-gray-500">Cold Leads (0-25 score)</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, company, city, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => handleStatusFilter('all')}
                size="sm"
              >
                All
              </Button>
              <Button 
                variant={statusFilter === 'hot' ? 'default' : 'outline'}
                onClick={() => handleStatusFilter('hot')}
                size="sm"
                className={statusFilter === 'hot' ? 'bg-red-500' : ''}
              >
                Hot
              </Button>
              <Button 
                variant={statusFilter === 'warm' ? 'default' : 'outline'}
                onClick={() => handleStatusFilter('warm')}
                size="sm"
                className={statusFilter === 'warm' ? 'bg-orange-500' : ''}
              >
                Warm
              </Button>
              <Button 
                variant={statusFilter === 'cold' ? 'default' : 'outline'}
                onClick={() => handleStatusFilter('cold')}
                size="sm"
                className={statusFilter === 'cold' ? 'bg-blue-500' : ''}
              >
                Cold
              </Button>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Lead</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Contact</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Details</th>
                  <th className="text-center p-4 text-sm font-semibold text-gray-600">Score</th>
                  <th className="text-center p-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-center p-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#c5a059]/20 rounded-full flex items-center justify-center">
                            <span className="text-[#c5a059] font-semibold">
                              {lead.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{lead.name || 'N/A'}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {lead.company || 'No company'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {lead.phone || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            {lead.email || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            {lead.city || 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1 text-sm">
                          <p><span className="text-gray-500">Budget:</span> {lead.budget_range || 'N/A'}</p>
                          <p><span className="text-gray-500">Seats:</span> {lead.seats_required || 'N/A'}</p>
                          <p><span className="text-gray-500">Timeline:</span> {lead.timeline || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 font-bold text-lg">
                          {lead.lead_score || 0}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(lead.lead_status)}`}>
                          {getStatusIcon(lead.lead_status)}
                          {lead.lead_status?.toUpperCase() || 'NEW'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <select 
                          value={lead.lead_status || ''}
                          onChange={(e) => updateLeadStatus(lead.session_id, e.target.value)}
                          className="text-sm border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                        >
                          <option value="cold">Cold</option>
                          <option value="warm">Warm</option>
                          <option value="hot">Hot</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Analytics Charts */}
        {dashboard && (dashboard.leads_by_city?.length > 0 || dashboard.leads_by_budget?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* Leads by City */}
            {dashboard.leads_by_city?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#c5a059]" />
                  Leads by City
                </h3>
                <div className="space-y-3">
                  {dashboard.leads_by_city.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-gray-600">{item.city}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#c5a059] rounded-full"
                            style={{ width: `${(item.count / dashboard.summary.total_leads) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leads by Budget */}
            {dashboard.leads_by_budget?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#c5a059]" />
                  Leads by Budget
                </h3>
                <div className="space-y-3">
                  {dashboard.leads_by_budget.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-gray-600">{item.budget}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${(item.count / dashboard.summary.total_leads) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
